import { Flex, Box } from '@cad-arc';
import {
    ConsolidatedLocationView,
    UnitLocationBootstrapResponse,
    UnitLocationUpdateRequest,
} from '@mark43/cad-api';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../forms/integrated-form-controls/form-location-select';
import { FormTextarea } from '../forms/integrated-form-controls/form-text-area';

interface UnitLocationFormProps {
    formData: UnitLocationBootstrapResponse | undefined;
    unitId: number;
}

export type UnitLocationFormType = UnitLocationUpdateRequest;

const MAX_COMMENTS_LENGTH = 255;

export function UnitLocationForm({ formData, unitId }: UnitLocationFormProps): JSX.Element {
    const {
        unitLocationFormLocationIsRequired,
        unitLocationFormLocationMustBeVerified,
        unitLocationFormCommentsAreRequired,
        subpremiseSupport,
    } = useEnabledFeatures();
    const { reset, control, watch, trigger } = useFormContext<UnitLocationFormType>();
    const consolidatedLocationView = watch('consolidatedLocationView');

    useEffect(
        function hydrateForm() {
            if (formData !== undefined) {
                reset(
                    {
                        comments: formData.comments,
                        consolidatedLocationView: formData.consolidatedLocationView,
                        subPremise: formData.subPremise,
                        unitId,
                    },
                    {
                        keepDefaultValues: true,
                    }
                );
            }
        },
        [unitId, reset, formData]
    );

    useEffect(() => {
        if (consolidatedLocationView) {
            trigger('consolidatedLocationView');
        }
    }, [consolidatedLocationView, trigger]);

    const validateLocation = useCallback(
        function validateLocation(
            currentLocation: ConsolidatedLocationView | undefined
        ): string | boolean {
            if (unitLocationFormLocationIsRequired && currentLocation === undefined) {
                return 'Required';
            }

            if (
                unitLocationFormLocationMustBeVerified &&
                currentLocation &&
                !currentLocation.areCoordinatesVerified
            ) {
                return 'Location must be verified';
            }
            return true;
        },
        [unitLocationFormLocationIsRequired, unitLocationFormLocationMustBeVerified]
    );

    return (
        <>
            <Box mb={2}>
                <FormLocationSelect
                    name="consolidatedLocationView"
                    control={control}
                    rules={{
                        required: unitLocationFormLocationIsRequired ? 'Required' : false,
                        validate: validateLocation,
                    }}
                    label="Location"
                    autoFocus
                />
                {!subpremiseSupport && (
                    <Flex direction="row" mt={2}>
                        <FormInput
                            name="subPremise"
                            control={control}
                            placeholder="Apt, Unit"
                            inputProps={{
                                maxLength: 200,
                            }}
                        />
                    </Flex>
                )}
            </Box>
            <FormTextarea
                name="comments"
                control={control}
                rules={{ required: unitLocationFormCommentsAreRequired ? 'Required' : false }}
                label="Comments"
                textareaProps={{
                    maxLength: MAX_COMMENTS_LENGTH,
                }}
            />
        </>
    );
}
